import { useQuery } from '@apollo/client'
import totalMonthlyUsageGql from 'gql/Report/totalMonthlyUsage.gql'
import subnetUsageGql from 'gql/Report/subnetUsage.gql'

interface MonthlyUsage {
    date: string
    total: number
}

interface UseTotalMonthlyUsageReturn {
    data: MonthlyUsage[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: any
    loading: boolean
    refetch: () => void
}

/**
 * Retrieves the total monthly usage data from the server.
 *
 * @return {UseTotalMonthlyUsageReturn} An object containing the total monthly usage data,
 * error status, loading state, and a function to refetch the data.
 */
export const useTotalMonthlyUsageService = (): UseTotalMonthlyUsageReturn => {
    const { data, error, loading, refetch } = useQuery(totalMonthlyUsageGql, {
        fetchPolicy: 'cache-and-network',
    })

    return {
        data: data?.totalMonthlyUsage || [],
        error,
        loading,
        refetch,
    }
}

/**
 * Retrieves the subnet usage data from the server.
 *
 * @return {Object} An object containing the subnet usage data, error status, loading state, and a function to refetch the data.
 * - `data` (Object): The subnet usage data.
 * - `error` (Error | undefined): The error, if any.
 * - `loading` (boolean): The loading status.
 * - `refetch` (() => Promise<ApolloQueryResult<SubnetUsageQuery>>): The refetch function.
 */
export const useSubnetUsageService = () => {
    const { data, error, loading, refetch } = useQuery(subnetUsageGql, {
        fetchPolicy: 'cache-and-network',
    })

    return {
        data: data?.subnetUsage.data || [],
        error,
        loading,
        refetch,
    }
}
